

export const DATE_LIMIT_MONTHS = 6;

/**
 * @function shiftMonths
 * @description Shifts an ISO `YYYY-MM-DD` date by whole months, staying in UTC and in the
 * plain-string format the native date inputs use. Month overflow follows
 * `Date`'s own rounding (Aug 31 + 6 -> Mar 3), which is fine for a boundary.
 * @param {string} isoDate
 * @param {number} months
 * **Called by:
 * @see {getModelDateLimits}
 * @memberof module:ShoreS
 * @returns {string} ISO `YYYY-MM-DD`.
 */
export const shiftMonths = (isoDate, months) => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() + months);
  return date.toISOString().slice(0, 10);
};

/**
 * @function getModelDateLimits
 * @description Widens the model's own temporal range by `DATE_LIMIT_MONTHS` on each side,
 * producing the window the user is allowed to search in.
 * @param {{start: string, end: string}|null|undefined} dateRange - The model's detected range.
 * **Called by:
 * @see {ShoreSDataChooser}
 * @see {ShoreSPeriodImageChooser}
 * @memberof module:ShoreS
 * @returns {{min: string, max: string}|null} The allowed window, or null when the model has none.
 */
export const getModelDateLimits = (dateRange) =>
  dateRange?.start && dateRange?.end
    ? {
        min: shiftMonths(dateRange.start, -DATE_LIMIT_MONTHS),
        max: shiftMonths(dateRange.end, DATE_LIMIT_MONTHS),
      }
    : null;

/**
 * @function isWithinLimits
 * @description Whether a date falls inside the allowed window. A null `limits` means the
 * model carries no temporal range, so nothing is restricted.
 * @param {string} value - `YYYY-MM-DD`.
 * @param {{min: string, max: string}|null} limits
 * **Called by:
 * @see {ShoreSDataChooser}
 * @see {ShoreSPeriodImageChooser}
 * @memberof module:ShoreS
 * @returns {boolean}
 */
export const isWithinLimits = (value, limits) => !limits || (value >= limits.min && value <= limits.max);

/**
 * @function getDateRangeErrors
 * @description Classifies why a start/end pair is invalid, so the UI can name the offending
 * field and reason instead of only turning a border red. Empty fields are not errors here —
 * "not filled in yet" is a separate, non-alarming state the callers gate on themselves.
 * @param {string} start - `YYYY-MM-DD`, possibly empty.
 * @param {string} end - `YYYY-MM-DD`, possibly empty.
 * @param {{min: string, max: string}|null} limits
 * **Called by:
 * @see {ShoreSDataChooser}
 * @see {ShoreSPeriodImageChooser}
 * @memberof module:ShoreS
 * @returns {{start: string|null, end: string|null}} Reason codes: 'outOfRange' | 'beforeStart'.
 */
export const getDateRangeErrors = (start, end, limits) => {
  const errors = { start: null, end: null };
  if (start !== '' && !isWithinLimits(start, limits)) errors.start = 'outOfRange';
  if (end !== '' && !isWithinLimits(end, limits)) errors.end = 'outOfRange';
  if (!errors.end && start !== '' && end !== '' && start > end) errors.end = 'beforeStart';
  return errors;
};
